import React, { useEffect, useState } from "react"
import type { ReactNode } from "react"
import { Minus, Plus, Smile, X } from "lucide-react"

import { HELP_TEXT, TAP_TARGET } from "@/lib/ui-tokens"
import { useRecordEvent } from "@/lib/use-record-event"
import { cn } from "@/lib/utils"
import { Alert, AlertDescription } from "@/shadcn/components/ui/alert"
import { Button } from "@/shadcn/components/ui/button"
import {
  ButtonGroup,
  ButtonGroupText,
} from "@/shadcn/components/ui/button-group"
import { Label } from "@/shadcn/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/shadcn/components/ui/radio-group"
import { Textarea } from "@/shadcn/components/ui/textarea"
import { openMessenger } from "app/utility/Intercom.js"

/*
 * "Cancel account" retention flow — the Figma redesign of the markup that used
 * to live in Cancellation/{CancellationModal,FeedbackForm,DiscountOffer,
 * PauseOption,GetHelp}.jsx. Behaviour is a straight port; only the presentation
 * changed. Steps map onto the old numeric views as:
 *
 *   step          old view   old component
 *   ────────────  ─────────  ─────────────────
 *   "interrupt"   1          CancellationModal
 *   "survey"      2          FeedbackForm
 *   "help"        6          GetHelp
 *   "discount"    3          DiscountOffer
 *   "pause"       4          PauseOption
 *   "confirm"     5          CancellationModal
 *
 * The survey routes to "help" for a technical issue and to "discount" for
 * everything else; "help" declines into "discount", "discount" into "pause",
 * and "pause" into "confirm".
 *
 * Each step is its own component so its draft state (a half-typed reason, a
 * chosen pause duration, an in-flight flag) unmounts with the step, the same way
 * it did when these were five separate files.
 */

/* ────────────────────────────────────────────────────────────── types ── */

export type CancelStep =
  | "interrupt"
  | "survey"
  | "help"
  | "discount"
  | "pause"
  | "confirm"

type CancelReason = {
  id: string
  label: string
  /**
   * Present when the reason needs a written follow-up. Its absence is what makes
   * Continue pass without any text, so this doubles as the old `needsFeedback`.
   */
  detail?: { label: string }
}

export type CancelAccountProps = {
  className?: string
  app: any
  user: any

  /** Where the account lands once cancelled — passed straight to `planChangeHandler`. */
  newPlanLevel: any
  newPlanTerm: any
  /**
   * The next billing date, already formatted for display. Formatting stays in
   * the JSX layer because `moment` is a webpack external with no types, and
   * pulling it into a ts-loader-compiled file would not typecheck.
   */
  nextBillingLabel: string

  applyCoupon: (data: { plan: any; code: string }) => void
  closeAllModals: () => void
  deleteApiError: (keys: string[]) => void
  getApiErrors: (keys: string[]) => any[]
  pauseSubscription: (data: { duration: number }) => void
  planChangeHandler: (data: {
    level: any
    term: any
    trackingAction: string
  }) => void

  /** Entry point for the flow. Anything but the default is for previewing a step. */
  initialStep?: CancelStep
}

/* ─────────────────────────────────────────────────────────── constants ── */

/**
 * Ids are the keys the old FeedbackForm used, because "issue" is the value the
 * survey branches on and both ids and labels are reported to analytics.
 */
const REASONS: CancelReason[] = [
  {
    id: "features",
    label: "Missing features",
    detail: { label: "Please let us know what features are missing" },
  },
  {
    id: "issue",
    label: "Technical issue",
    detail: { label: "Please let us know what issue you're having" },
  },
  { id: "underutilized", label: "Not using all the features" },
  {
    id: "cost",
    label: "Too expensive",
    // The one reason the redesign added a follow-up to. There is no prod copy to
    // inherit here, so the prompt is the design's own.
    detail: { label: "Please let us know what would work better for you" },
  },
  {
    id: "competitor",
    label: "Found another solution",
    detail: { label: "What solution are you planning to use?" },
  },
  {
    id: "somethingElse",
    label: "Something else",
    detail: { label: "Please let us know what issue you're having" },
  },
]

const REASON_REQUIRED = "Please let us know why you're leaving."
const DETAIL_REQUIRED = "Please provide some feedback"

const PAUSE_MIN = 1
const PAUSE_MAX = 3

/**
 * Both Recurly-backed calls surface failures identically: the first API error
 * carries either a `validation` or a `recurly` map and only the first entry of
 * whichever is present was ever shown. This was duplicated verbatim in
 * DiscountOffer and PauseOption.
 */
function firstApiErrorMessage(errors: any[]): string {
  const source = errors[0]?.errors?.validation ?? errors[0]?.errors?.recurly

  if (!source) {
    return ""
  }

  const keys = Object.keys(source)

  return keys.length ? source[keys[0]] : ""
}

/* ────────────────────────────────────────────────────────────── parts ── */

/** The 440-wide alert-dialog surface shared by "interrupt" and "help". */
function DialogCard({ children }: { children: ReactNode }) {
  return (
    <div className="tw:flex tw:w-full tw:max-w-[440px] tw:flex-col tw:gap-6 tw:rounded-xl tw:border tw:border-border tw:bg-card tw:p-4 tw:shadow-xs tw:md:p-6">
      {children}
    </div>
  )
}

/**
 * Right-aligned dialog footer. Stacks below `sm`: side by side the two labels
 * need ~313px, and a phone-width card leaves 271px inside its padding.
 */
function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <div className="tw:flex tw:w-full tw:flex-col-reverse tw:gap-2 tw:sm:flex-row tw:sm:flex-wrap tw:sm:justify-end">
      {children}
    </div>
  )
}

/**
 * Every action button: full width at mobile, hugging its label from `sm`.
 *
 * h-auto with a min-height rather than a fixed height, and normal wrapping
 * rather than the primitive's nowrap, so a label too long for one line grows the
 * button instead of overflowing its container.
 */
const ACTION =
  "tw:h-auto tw:min-h-11 tw:w-full tw:max-w-full tw:py-1.5 tw:whitespace-normal tw:sm:w-auto tw:md:min-h-8"

/** The 560-wide centred column shared by the branch and confirm steps. */
function CenteredColumn({ children }: { children: ReactNode }) {
  return (
    <div className="tw:flex tw:w-full tw:max-w-[560px] tw:flex-col tw:items-center tw:gap-6">
      {children}
    </div>
  )
}

function StepHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="tw:text-2xl tw:font-semibold tw:text-balance tw:text-center tw:text-foreground">
      {children}
    </h2>
  )
}

/** Recurly's own wording, surfaced verbatim as it was in the old markup. */
function ApiError({ message }: { message: string }) {
  if (!message) {
    return null
  }

  return (
    <Alert variant="destructive" className="tw:w-full tw:max-w-[320px]">
      <AlertDescription className="tw:text-destructive">
        {message}
      </AlertDescription>
    </Alert>
  )
}

/* ─────────────────────────────────────────────────── step · interrupt ── */

function InterruptStep({
  onDecline,
  onKeep,
}: {
  onDecline: () => void
  onKeep: () => void
}) {
  return (
    <DialogCard>
      <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
        <p className="tw:text-lg tw:font-semibold tw:text-foreground">
          Do you want to keep posting at your current levels?
        </p>
        <p className="tw:text-sm tw:text-muted-foreground">
          Canceling your dlvr.it plan will move you to the Launch plan with
          limited posting and features.
        </p>
      </div>
      <DialogFooter>
        <Button variant="outline" className={ACTION} onClick={onDecline}>
          No, move to limited plan
        </Button>
        <Button className={ACTION} onClick={onKeep}>
          Yes! Keep posting
        </Button>
      </DialogFooter>
    </DialogCard>
  )
}

/* ──────────────────────────────────────────────────────── step · help ── */

function HelpStep({ onDecline }: { onDecline: () => void }) {
  return (
    <DialogCard>
      <div className="tw:flex tw:w-full tw:gap-4">
        <span
          className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md tw:bg-muted tw:text-foreground"
          aria-hidden
        >
          <Smile className="tw:size-6" />
        </span>
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-2">
          <p className="tw:text-lg tw:font-semibold tw:text-foreground">
            Let us help!
          </p>
          <p className="tw:text-sm tw:text-muted-foreground">
            Our support team is here to help you resolve any issues you are
            having.
          </p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" className={ACTION} onClick={onDecline}>
          No thanks
        </Button>
        <Button className={ACTION} onClick={() => openMessenger()}>
          Open a priority ticket
        </Button>
      </DialogFooter>
    </DialogCard>
  )
}

/* ──────────────────────────────────────────────────── step · discount ── */

type DiscountOffer = { amount: string; caption: string; coupon: string }

/**
 * Null for any term we cannot discount, which is what makes the step skip itself.
 *
 * The design draws the card as amount + one caption, so the old pitch and the
 * separate "Limited time offer" line are folded together — the caption carries
 * both the urgency and the span the discount covers.
 */
function discountFor(constants: any, planTerm: any): DiscountOffer | null {
  if (planTerm === constants.SUBSCRIPTION_TERM_MONTHLY) {
    return {
      amount: "50% OFF",
      caption: "Limited-time offer on your next 3 months",
      coupon: "boost_monthly",
    }
  }

  if (planTerm === constants.SUBSCRIPTION_TERM_ANNUAL) {
    return {
      amount: "30% OFF",
      caption: "Limited-time offer on your next year",
      coupon: "boost_annual",
    }
  }

  return null
}

function DiscountStep({
  app,
  user,
  applyCoupon,
  deleteApiError,
  getApiErrors,
  onDecline,
}: {
  app: any
  user: any
  applyCoupon: CancelAccountProps["applyCoupon"]
  deleteApiError: CancelAccountProps["deleteApiError"]
  getApiErrors: CancelAccountProps["getApiErrors"]
  onDecline: () => void
}) {
  const [isWorking, setIsWorking] = useState(false)
  const [redemptionError, setRedemptionError] = useState("")

  const offer = discountFor(app.config.constants, user.plan.planTerm)

  // No monthly or annual subscription means there is nothing we can discount,
  // so the step skips itself rather than showing an empty offer.
  const hasOffer = offer !== null

  useEffect(() => {
    if (!hasOffer) {
      onDecline()
    }
  }, [hasOffer])

  useEffect(() => {
    const couponErrors = getApiErrors(["SUB_APPLY_COUPON"])

    if (isWorking) {
      setIsWorking(false)
    }

    if (couponErrors.length) {
      const message = firstApiErrorMessage(couponErrors)

      if (message) {
        setRedemptionError(message)
      }

      deleteApiError(["SUB_APPLY_COUPON"])
    }
  }, [app.messages])

  if (!offer) {
    return null
  }

  const claimOffer = () => {
    setIsWorking(true)
    setRedemptionError("")
    applyCoupon({ plan: user.plan.planCode, code: offer.coupon })
  }

  return (
    <CenteredColumn>
      <StepHeading>
        Before you cancel, we may have a better solution for you.
      </StepHeading>
      <ApiError message={redemptionError} />
      <div className="tw:flex tw:w-full tw:max-w-[320px] tw:flex-col tw:items-center tw:gap-2 tw:rounded-xl tw:border tw:border-border tw:bg-card tw:p-6 tw:shadow-xs">
        <p className="tw:text-[32px] tw:leading-none tw:font-semibold tw:text-foreground">
          {offer.amount}
        </p>
        <p className={cn("tw:text-center tw:font-medium", HELP_TEXT)}>
          {offer.caption}
        </p>
      </div>
      <Button className={ACTION} loading={isWorking} onClick={claimOffer}>
        Claim offer
      </Button>
      {!isWorking ? (
        <Button variant="link" className={TAP_TARGET} onClick={onDecline}>
          No thanks
        </Button>
      ) : null}
    </CenteredColumn>
  )
}

/* ─────────────────────────────────────────────────────── step · pause ── */

function PauseStep({
  app,
  deleteApiError,
  getApiErrors,
  pauseSubscription,
  onDecline,
}: {
  app: any
  deleteApiError: CancelAccountProps["deleteApiError"]
  getApiErrors: CancelAccountProps["getApiErrors"]
  pauseSubscription: CancelAccountProps["pauseSubscription"]
  onDecline: () => void
}) {
  const [duration, setDuration] = useState(PAUSE_MIN)
  const [isWorking, setIsWorking] = useState(false)
  const [pauseError, setPauseError] = useState("")

  useEffect(() => {
    const pauseErrors = getApiErrors(["SUB_PAUSE_SUBSCRIPTION"])

    if (isWorking) {
      setIsWorking(false)
    }

    if (pauseErrors.length) {
      const message = firstApiErrorMessage(pauseErrors)

      if (message) {
        setPauseError(message)
      }

      deleteApiError(["SUB_PAUSE_SUBSCRIPTION"])
    }
  }, [app.messages])

  const unitLabel = duration > 1 ? "months" : "month"

  return (
    <CenteredColumn>
      <StepHeading>
        Just need a little break? You can pause your account.
      </StepHeading>
      <ApiError message={pauseError} />
      <div className="tw:flex tw:w-full tw:max-w-[320px] tw:flex-col tw:items-center tw:gap-4 tw:rounded-xl tw:border tw:border-border tw:bg-card tw:p-6 tw:shadow-xs">
        <p className="tw:text-sm tw:font-medium tw:text-muted-foreground">
          PAUSE ACCOUNT
        </p>
        <div className="tw:flex tw:items-center tw:gap-2">
          <ButtonGroup>
            <Button
              variant="outline"
              size="icon"
              className="tw:size-11 tw:md:size-8"
              aria-label="Decrease pause duration"
              disabled={duration <= PAUSE_MIN}
              onClick={() => setDuration((n) => Math.max(PAUSE_MIN, n - 1))}
            >
              <Minus />
            </Button>
            {/* ButtonGroupText, not a Button: the count reports state rather
                than affording a press. bg-background because the group's own
                default is muted and the design draws this segment white. */}
            <ButtonGroupText
              className="tw:h-11 tw:bg-background tw:md:h-8"
              aria-live="polite"
            >
              {duration}
            </ButtonGroupText>
            <Button
              variant="outline"
              size="icon"
              className="tw:size-11 tw:md:size-8"
              aria-label="Increase pause duration"
              disabled={duration >= PAUSE_MAX}
              onClick={() => setDuration((n) => Math.min(PAUSE_MAX, n + 1))}
            >
              <Plus />
            </Button>
          </ButtonGroup>
          <span className="tw:text-base tw:text-foreground">{unitLabel}</span>
        </div>
        <p className="tw:text-center tw:text-sm tw:text-muted-foreground">
          You will not be billed while paused
        </p>
      </div>
      <Button
        className={ACTION}
        loading={isWorking}
        onClick={() => {
          setIsWorking(true)
          setPauseError("")
          pauseSubscription({ duration })
        }}
      >
        Pause account
      </Button>
      {!isWorking ? (
        <Button variant="link" className={TAP_TARGET} onClick={onDecline}>
          No thanks, cancel
        </Button>
      ) : null}
    </CenteredColumn>
  )
}

/* ────────────────────────────────────────────────────── step · survey ── */

function SurveyStep({
  onKeep,
  onContinue,
}: {
  onKeep: () => void
  onContinue: (reasonId: string) => void
}) {
  const recordEvent = useRecordEvent()
  const [selected, setSelected] = useState("")
  const [detailCopy, setDetailCopy] = useState("")
  const [detailError, setDetailError] = useState("")
  const [requiredError, setRequiredError] = useState("")

  const onContinueClick = () => {
    setDetailError("")

    if (selected === "") {
      setRequiredError(REASON_REQUIRED)
      return
    }

    const reason = REASONS.find((candidate) => candidate.id === selected)

    if (!reason) {
      return
    }

    if (reason.detail && !detailCopy) {
      setDetailError(DETAIL_REQUIRED)
      return
    }

    recordEvent("Cancellation Feedback", {
      reason: reason.label,
      detail: detailCopy,
    })

    onContinue(reason.id)
  }

  return (
    <div className="tw:flex tw:w-full tw:max-w-[560px] tw:flex-col tw:gap-6">
      <div className="tw:flex tw:flex-col tw:gap-1.5 tw:text-center">
        <StepHeading>
          We're sorry to see you scale back your posting.
        </StepHeading>
        <p className="tw:text-sm tw:text-muted-foreground">
          Please let us know why you're changing plans and if there's anything
          we can improve.
        </p>
      </div>

      {requiredError ? (
        <p className="tw:text-sm tw:text-destructive">{requiredError}</p>
      ) : null}

      <RadioGroup
        value={selected}
        onValueChange={(value) => {
          // Switching reasons drops the draft, so the follow-up text can never
          // be filed against a reason it was not written for.
          setSelected(value)
          setDetailCopy("")
          setDetailError("")
          setRequiredError("")
        }}
      >
        {REASONS.map((reason) => {
          const open = reason.id === selected && reason.detail

          return (
            <div key={reason.id} className="tw:flex tw:flex-col tw:gap-2">
              <div className="tw:flex tw:items-center tw:gap-2">
                <RadioGroupItem
                  value={reason.id}
                  id={`cancel-reason-${reason.id}`}
                />
                <Label
                  htmlFor={`cancel-reason-${reason.id}`}
                  className="tw:text-sm tw:font-medium tw:text-foreground"
                >
                  {reason.label}
                </Label>
              </div>
              {/* The follow-up sits under the option that asked it, indented to
                  the label's left edge (16px radio + 8px gap), so the pair reads
                  as one thing. */}
              {open ? (
                <div className="tw:flex tw:w-full tw:flex-col tw:gap-2 tw:pl-6">
                  <p className="tw:text-sm tw:text-muted-foreground">
                    {reason.detail?.label}
                  </p>
                  <Textarea
                    className="tw:min-h-24"
                    value={detailCopy}
                    aria-invalid={detailError ? true : undefined}
                    onChange={(e) => {
                      setDetailCopy(e.currentTarget.value)
                      setDetailError("")
                    }}
                    /* The textarea lives inside the RadioGroup so it stays
                       attached to its option, but Radix's roving focus handles
                       arrow keys on the group root — without this, pressing Left
                       or Up while typing would jump to the previous radio. */
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                  {detailError ? (
                    <p className="tw:text-sm tw:text-destructive">
                      {detailError}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          )
        })}
      </RadioGroup>

      <div className="tw:flex tw:flex-col-reverse tw:gap-2 tw:sm:flex-row tw:sm:flex-wrap tw:sm:justify-center">
        <Button variant="outline" className={ACTION} onClick={onContinueClick}>
          Continue
        </Button>
        <Button className={ACTION} onClick={onKeep}>
          Keep my plan
        </Button>
      </div>
    </div>
  )
}

/* ───────────────────────────────────────────────────── step · confirm ── */

function ConfirmStep({
  isWorking,
  nextBillingLabel,
  onConfirm,
}: {
  isWorking: boolean
  nextBillingLabel: string
  onConfirm: () => void
}) {
  return (
    <CenteredColumn>
      <StepHeading>We understand your needs can change.</StepHeading>
      {/* One paragraph per the design. "Launch plan" rather than the design's
          "our limited free plan" so it matches what the interrupt step promised. */}
      <p className="tw:text-center tw:text-sm tw:text-muted-foreground">
        Your plan will be canceled and you'll move to the Launch plan with
        limited features and posting on {nextBillingLabel}. When you're ready to
        post more, we're here for you.
      </p>
      <Button
        variant="outline"
        className={ACTION}
        loading={isWorking}
        onClick={onConfirm}
      >
        Cancel plan
      </Button>
    </CenteredColumn>
  )
}

/* ─────────────────────────────────────────────────────────── export ── */

export default function CancelAccount({
  className,
  app,
  user,
  newPlanLevel,
  newPlanTerm,
  nextBillingLabel,
  applyCoupon,
  closeAllModals,
  deleteApiError,
  getApiErrors,
  pauseSubscription,
  planChangeHandler,
  initialStep = "interrupt",
}: CancelAccountProps) {
  const [step, setStep] = useState<CancelStep>(initialStep)
  const [isCanceling, setIsCanceling] = useState(false)

  let body: ReactNode = null

  switch (step) {
    case "survey":
      body = (
        <SurveyStep
          onKeep={closeAllModals}
          onContinue={(reasonId) =>
            setStep(reasonId === "issue" ? "help" : "discount")
          }
        />
      )
      break
    case "help":
      body = <HelpStep onDecline={() => setStep("discount")} />
      break
    case "discount":
      body = (
        <DiscountStep
          app={app}
          user={user}
          applyCoupon={applyCoupon}
          deleteApiError={deleteApiError}
          getApiErrors={getApiErrors}
          onDecline={() => setStep("pause")}
        />
      )
      break
    case "pause":
      body = (
        <PauseStep
          app={app}
          deleteApiError={deleteApiError}
          getApiErrors={getApiErrors}
          pauseSubscription={pauseSubscription}
          onDecline={() => setStep("confirm")}
        />
      )
      break
    case "confirm":
      body = (
        <ConfirmStep
          isWorking={isCanceling}
          nextBillingLabel={nextBillingLabel}
          onConfirm={() => {
            setIsCanceling(true)
            planChangeHandler({
              level: newPlanLevel,
              term: newPlanTerm,
              trackingAction: "downgrade",
            })
          }}
        />
      )
      break
    default:
      body = (
        <InterruptStep
          onDecline={() => setStep("survey")}
          onKeep={closeAllModals}
        />
      )
      break
  }

  return (
    <div
      className={cn(
        // max-w matches the Figma frame (1040). min-h-dvh because the flow owns
        // the whole full-page modal, which is fixed to the viewport — that is
        // what gives justify-center something to centre against.
        "tw:relative tw:mx-auto tw:flex tw:min-h-dvh tw:w-full tw:max-w-[1040px] tw:flex-col tw:items-center tw:justify-center tw:bg-card tw:px-5 tw:py-8 tw:font-sans tw:text-card-foreground tw:md:px-10 tw:md:py-16",
        className
      )}
    >
      {/* Figma draws the dismiss on the interrupt frame only, but every step of
          the old modal was closable from the FullPageModal chrome, so it stays
          on all of them. */}
      <Button
        variant="ghost"
        size="icon"
        aria-label="Close"
        onClick={closeAllModals}
        className="tw:absolute tw:top-3 tw:right-3 tw:size-11 tw:md:top-8 tw:md:right-8 tw:md:size-8"
      >
        <X />
      </Button>
      {body}
    </div>
  )
}
